import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Printer, Loader2, Package, ShoppingCart, AlertCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { apiFetch } from '../lib/api';

type Mode = 'order' | 'delivery';

interface SummaryProduct {
  productId: number;
  name: string;
  quantity: number;
  orderCount: number;
}

interface SummaryData {
  date: string;
  mode: Mode;
  products: SummaryProduct[];
  totalQty: number;
  orderCount: number;
}

// 取香港今日日期 (UTC+8)
function hkToday(): string {
  return new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
}

// 模式 → i18n key（落單日/送貨日）
const MODE_LABELS: Record<Mode, string> = {
  order: 'partner:supplier.modeOrder',
  delivery: 'partner:supplier.modeDelivery',
};

const SupplierPrepSummary: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = localStorage.getItem('admin_token');

  const [date, setDate] = useState<string>(hkToday());
  const [mode, setMode] = useState<Mode>('delivery');
  const [data, setData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  }, [navigate]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/supplier/summary?date=${date}&mode=${mode}`);
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('partner:supplier.prep.loadFailed'));
      setData(json as SummaryData);
    } catch (e: any) {
      setError(e.message || t('partner:supplier.prep.loadFailed'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date, mode, handleUnauthorized]);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadSummary();
  }, [token, navigate, loadSummary]);

  return (
    <AdminLayout currentPage="supplier-prep-summary" onLogout={handleUnauthorized}>
      <div className="p-4 md:p-8">
        {/* 頁首 */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 print:hidden">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <ClipboardList className="w-6 h-6 text-orange-600" />
              {t('partner:supplier.prep.title')}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('partner:supplier.prep.intro')}
            </p>
          </div>
          <LanguageSwitcher size="sm" />
        </div>

        {/* 控制列 */}
        <div className="mb-6 flex flex-wrap items-center gap-3 print:hidden">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
          <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
            {(Object.keys(MODE_LABELS) as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  mode === m
                    ? 'bg-orange-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {t(MODE_LABELS[m])}
              </button>
            ))}
          </div>
          <button
            onClick={() => window.print()}
            className="print:hidden flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <Printer className="w-4 h-4" />
            {t('partner:supplier.prep.print')}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300 print:hidden">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-600 dark:text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            {t('partner:supplier.loading')}
          </div>
        ) : (
          <>
            {/* 統計卡片 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 print:hidden">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Package className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{t('partner:supplier.prep.totalPortions')}</div>
                    <div className="text-3xl font-bold text-gray-800 dark:text-white">{data?.totalQty ?? 0}</div>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{t('partner:supplier.prep.orderCount')}</div>
                    <div className="text-3xl font-bold text-gray-800 dark:text-white">{data?.orderCount ?? 0}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* 備貨表格（列印區） */}
            <div id="prep-sheet-print" className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* 列印專用標題 */}
              <div className="hidden print:block px-6 py-4">
                <h2 className="text-xl font-bold text-gray-800">
                  {t('partner:supplier.prep.sheetTitle', { date, mode: t(MODE_LABELS[mode]) })}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('partner:supplier.prep.sheetSummary', { qty: data?.totalQty ?? 0, count: data?.orderCount ?? 0 })}
                </p>
              </div>

              {data && data.products.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-800 dark:text-white">{t('partner:supplier.prep.colProduct')}</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800 dark:text-white">{t('partner:supplier.prep.totalPortions')}</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-800 dark:text-white">{t('partner:supplier.prep.colOrders')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {data.products.map((p) => (
                      <tr key={p.productId}>
                        <td className="px-6 py-4 text-gray-800 dark:text-white">{p.name}</td>
                        <td className="px-6 py-4 text-right text-2xl font-bold text-orange-600">{p.quantity}</td>
                        <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-400">{p.orderCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-6 py-16 text-center text-gray-600 dark:text-gray-400">
                  {t('partner:supplier.prep.empty')}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`@media print { body * { visibility: hidden; } #prep-sheet-print, #prep-sheet-print * { visibility: visible; } #prep-sheet-print { position: absolute; left: 0; top: 0; width: 100%; } }`}</style>
    </AdminLayout>
  );
};

export default SupplierPrepSummary;
